<template>
    <b-row>
        <b-col>
            <b-row v-if="mode == 'associate'">
                <b-col>
                    <b>File Received: </b><span>{{ queueItem.ingested | formatDate("L LT") }}</span>;
                    <b> Source: </b><span>{{ queueItem.source }}</span>
                </b-col>
            </b-row>

            <!-- Select patient -->
            <b-row v-if="mode == 'associate'">
                <b-col>
                    <SearchComponent v-model="selectedPatient" :label="`Select a Patient`" search-u-r-l="/Patient"
                        create-new-object createNewLabel="Create New Patient"
                        @createNewObject="showPatientForm=true"
                        @input="loadDocs" />
                </b-col>
            </b-row>
            <div v-else>
                <b>Patient: </b> <span>{{ patient.displayNamesForPerson() }}</span>
            </div>

            <PatientFormComponent v-if="showPatientForm"
                :button-titles="patientButtonTitles"
                create-new-patient hide-insurance
                @close="closePatientForm"
                @created="onPatientCreated" />

            <!-- Upload component -->
            <div v-if="!listOnly" class="mb-3">
                <b-row v-if="mode == 'new'" class="mb-2">
                    <b-col>
                        <label for="uploadFile">Upload File</label>
                        <!-- Styled -->
                        <b-form-file id="uploadFile" v-model="newDocument" accept=".pdf"
                            drop-placeholder="Drop file here..."
                            placeholder="Choose patient's document or drop it here...">
                        </b-form-file>
                    </b-col>
                </b-row>

                <!-- Indicate File Name-->
                <b-row>
                    <b-col>
                        <!-- Styled -->
                        <fg-input v-model="fileName" :class="fileNameInputClass" label="File Name" required></fg-input>
                    </b-col>
                </b-row>

                <b-row v-if="availableTags?.length">
                    <b-col>
                        <label>Tags<span v-if="tagsRequired"> *</span></label>
                        <multiselect v-model="selectedTags" :options="availableTags" multiple />
                    </b-col>
                </b-row>

                <!-- Save button @click="saveDocument"-->
                <div v-if="!isUploading">
                    <b-button :disabled="disableSave" variant="primary" @click="submit">Save Document</b-button>
                    <b-button variant="dark" @click="close">Cancel</b-button>
                </div>
                <div v-else>
                    <b-spinner class="mr-2" label="Spinning"></b-spinner>
                    <strong>Uploading...</strong>
                </div>
            </div>

            <div v-if="documentsHaveTags" class="d-flex field-filters flex-wrap">
                <b-badge :variant="filterTags.length ? 'secondary' : 'primary'" @click="filterTags = []">
                    All Documents
                </b-badge>
                <b-badge v-for="i in documentsUniqueTags" :key="i"
                    :variant="!filterTags.includes(i) ? 'secondary' : 'primary'" @click="toggleFilterTag(i)">
                    {{ i }}
                    <span class="count">{{ getDocumentCountForTag(i) }}</span>
                </b-badge>
            </div>
            <b-modal id="fax-modal" title="Fax Number" ok-title="Send Fax" @ok="faxDocument" @hidden="clearFaxNumber">
                <b-input v-model="faxNumber"></b-input>
            </b-modal>
            <!-- Documents Grid , row event open file in new tab -->
            <b-table :empty-text="emptyText" :fields="fields" :items="filteredPatientDocuments" responsive="true"
                show-empty striped primary-key="id" sort-by="created" :sort-desc="true">
                <template #cell(fileName)="data">
                    <b-input v-if="editingRowIndex === data.index" v-model="editingPatientDocument.fileName"
                        size="sm"></b-input>
                    <strong v-else>
                        <a href="" @click.prevent="openDocument(data.item)">
                            {{ data.value }}
                        </a>
                    </strong>
                </template>
                <template #cell(selected)="row">
                    <input type="checkbox" :value="row.item.imageId" v-model="selectedImageIds"></input>
                </template>
                <template #cell(view)='data'>
                    <slot name="cell(view)" v-bind:data="data">
                        <div v-if="editingRowIndex === -1" class="table-icon-actions">
                            <a v-b-tooltip.hover href="" title="Fax" @click.prevent="$bvModal.show('fax-modal'); faxImageId = data.item.imageId">
                                <b-icon icon="share" variant="primary" />
                            </a>
                            <a v-b-tooltip.hover href="" title="View" @click.prevent="openDocument(data.item)">
                                <b-icon icon="eye" variant="primary"></b-icon>
                            </a>
                            <a v-b-tooltip.hover href="" title="Edit"
                                @click.prevent="startEditDocument(data.item, data.index)">
                                <b-icon icon="pencil" variant="primary"></b-icon>
                            </a>
                        </div>
                        <div v-if="editingRowIndex === data.index" class="table-icon-actions">
                            <a v-b-tooltip.hover href="" title="Save" @click.prevent="editDocument">
                                <b-icon icon="save" variant="primary"></b-icon>
                            </a>
                            <a v-b-tooltip.hover href="" title="Cancel" @click.prevent="editingRowIndex = -1">
                                <b-icon icon="x-lg" variant=""></b-icon>
                            </a>
                        </div>
                    </slot>
                </template>
                <template #cell(created)="data">
                    {{ data.value }}
                    <div class="text-muted">
                        {{ data.item.createdBy }}
                    </div>
                </template>
                <template #cell(tags)="data">
                    <multiselect v-if="editingRowIndex === data.index" v-model="editingPatientDocument.tags"
                        :options="availableTags" multiple />
                    <div v-else class="d-flex flex-wrap" style="gap: 5px;">
                        <b-badge v-for="i in data.value" :key="i" variant="primary">
                            {{ i }}
                        </b-badge>
                    </div>
                </template>
            </b-table>
        </b-col>
    </b-row>
</template>

<script lang="ts">
import axios from 'axios';
import moment from "moment";
import { toRaw } from 'vue';
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import SearchComponent from '@/components/SearchComponent.vue';
import Multiselect from "vue-multiselect";
import { QueueItem } from '@/models/QueueItem';
import { PatientDocument } from '@/models/PatientDocument';
import { Patient } from '@/models/Patient';
import PatientFormComponent from "@/components/Patient/PatientFormComponent.vue";

@Component({
    name: "PatientDocumentsForm",
    components: {
        PatientFormComponent,
        SearchComponent,
        Multiselect,
    },
})
export default class PatientDocumentsForm extends Vue {
    @Prop({ default: 'patient_documents_modal' }) modalId!: string;
    @Prop({
        default: false,
        type: Boolean,
    }) listOnly!: boolean;
    @Prop() private patient!: Patient;
    @Prop() private queueItem!: QueueItem | null;
    @Prop({
        default: false,
        type: Boolean,
    }) private selectable!: boolean;
    private selectedPatient: Patient = new Patient();
    private newDocument: File = new File([], "");
    private fileName: string = '';
    private isUploading = false;
    private patientDocuments = [] as PatientDocument[];
    private selectedImageIds: number[] = [];
    private availableTags: string[] = [];
    private selectedTags: string[] = [];
    private filterTags: string[] = [];
    private editingRowIndex = -1;
    private editingPatientDocument = new PatientDocument();
    protected showPatientForm = false;
    private faxNumber: string = "";
    private faxImageId: number = 0;
    private requirePatientDocumentTags = false;

    protected patientButtonTitles = {
        new: "",
        clear: "",
        save: "Save Patient",
        cancel: "Close Patient Form",
    };

    closePatientForm() {
        this.showPatientForm = false;
        this.selectedPatient = new Patient();
    }

    onPatientCreated(newPatient: Patient) {
        this.showPatientForm = false;
        this.selectedPatient = newPatient;
        this.$bvModal.hide("create-patient");
    }

    get fields(): any[] {
        const resp = [];
        if (this.selectable) {
            resp.push(
                {
                    label: "Select",
                    key: "selected",
                },
            );
        }
        resp.push(
            {
                label: "File Name",
                key: "fileName",
                sortable: true,
                class: "file-name",
            },
            {
                key: "tags",
                hidden: !this.availableTags.length,
                class: "tags",
            },
            {
                label: "Uploaded",
                key: "created",
                sortable: true,
                formatter: (d: any) => moment.utc(d).local().format("L LT"),
                class: "uploaded",
            },
            {
                key: "view",
                label: "Actions",
                class: "actions",
            },
        );
        return resp.filter(f => f.hidden !== true);
    }

    get documentsHaveTags() {
        return this.patientDocuments.some(pd => pd.tags?.length);
    }

    get documentsUniqueTags() {
        const tags: string[] = [];

        this.patientDocuments.forEach(pd => {
            pd.tags?.forEach(t => {
                const tag = t.toLowerCase().trim();
                if (tags.includes(tag)) return;
                tags.push(tag);
            });
        });

        tags.sort();

        return tags;
    }

    get disableSave() {
        // Check if tags are required and none selected
        const tagsRequired = this.requirePatientDocumentTags && this.availableTags.length > 0;
        const hasRequiredTags = !tagsRequired || this.selectedTags.length > 0;
        
        if (this.mode == 'new') {
            return !(this.newDocument.size > 0 && this.isValidFileName && hasRequiredTags);
        } else if (this.mode == 'associate') {
            return !(this.patientID && this.isValidFileName && this.queueItem?.id && hasRequiredTags);
        }
    }

    get emptyText(): string {
        return this.patient?.id ? 'No documents for this patient.' : 'Select a patient.';
    }

    get patientID(): string | number | null {
        return this.patient?.id || this.selectedPatient?.id;
    }

    get mode(): string | null {
        if (!this.patient?.id && this.queueItem) {
            return 'associate';
        } else if (this.patient?.id && !this.queueItem) {
            return 'new';
        } else {
            return null;
        }
    }

    get isValidFileName(): boolean {
        const regex = /^[\w,\s-]+$/;
        const validFileName: boolean = regex.test(this.fileName?.trim());
        return validFileName;
    }

    get fileNameInputClass(): string {
        const val = this.isValidFileName;
        return val ? "has-success" : "has-danger";
    }

    get filteredPatientDocuments() {
        if (!this.filterTags.length) return this.patientDocuments;
        return this.patientDocuments.filter(pd => pd.tags?.some(t => this.filterTags.includes(t.toLowerCase().trim())));
    }

    get tagsRequired() {
        return this.requirePatientDocumentTags && this.availableTags.length > 0;
    }

    created() {
        //console.log('Pat docs form created');
        this.getTags();
        this.loadDocs();
        this.getConfiguration();
    }

    @Watch("selectedImageIds")
    @Emit("selectedImageIdsUpdate")
    updateIds(newValue: number[], _oldValue: number[]) {
        return newValue;
    }

    @Watch('patientID')
    async loadDocs() {
        if (!this.patientID) return;

        try {
            const res = await axios.get<Array<PatientDocument>>(`/patient/${this.patientID}/get-documents/`);
            if (res.data?.length) {
                this.patientDocuments = res.data.map(doc => new PatientDocument(doc))
                    .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
            }
        } catch (err) {
            console.error("Error while loading patient documents.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async submit() {
        if (this.mode == 'associate') {
            await this.associateDocument();
        } else if (this.mode == 'new') {
            await this.saveDocument();
        }
    }

    async saveDocument() {
        if (this.disableSave) return;

        // Upload custom table if there is any
        const formData = new FormData();

        formData.append('document', this.newDocument);
        formData.append('fileName', this.fileName.trim());
        formData.append('tags', JSON.stringify(this.selectedTags));
        const errorMsg = "Error while uploading new patient's document.";

        this.isUploading = true;
        try {
            const response = await axios.post<PatientDocument>(`/patient/${this.patientID}/add-document/`
                , formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (response.data?.id) {
                this.resetForm(response.data);
                this.loadDocs();
            } else {
                console.error(errorMsg, response);
            }
        } catch (err) {
            console.error(errorMsg, err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isUploading = false;
        }
    }

    async associateDocument() {
        if (this.disableSave) return;

        const errorMsg = "Error while associating selected document to the patient.";
        const pd = new PatientDocument();
        pd.fileName = this.fileName.trim();
        pd.tags = this.selectedTags;

        this.isUploading = true;

        try {
            const response = await axios.post<PatientDocument>(`/patient/${this.patientID}/associate-document/${this.queueItem?.id}`, pd);
            if (response.data?.id) {
                this.resetForm(response.data);
                this.$bvModal.hide(this.modalId);
            } else {
                console.error(errorMsg, response);
            }
        } catch (err) {
            console.error(errorMsg, err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isUploading = false;
        }
    }

    resetForm(resData: PatientDocument) {
        this.$notification(NotificationOptions.successSaveNotificationPreset('Patient Document'));
        this.newDocument = new File([], "");
        this.fileName = '';
        this.$emit('on-save', resData);
    }

    async faxDocument() {
        try {
            await axios.post("api/Fax/Send", {
                imageId: this.faxImageId,
                faxNumber: this.faxNumber,
            });
            this.$notification(NotificationOptions.success("Fax sent successfully."));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    clearFaxNumber() {
        this.faxNumber = '';
    }

    async openDocument(pd: PatientDocument) {
        try {
            const patientId = this.patientID;
            if (!patientId) {
                this.$notification(NotificationOptions.error("No patient selected"));
                return;
            }

            const res = await axios.get(`patient/${patientId}/document/${pd.imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(res.data);
            window.open(objectUrl, '_blank');
        } catch (err) {
            console.error("Error while getting document to show.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async getTags() {
        try {
            const response = await axios.get("/Configuration/PatientDocumentTags");
            this.availableTags = response.data;
        } catch (err) {
            console.error(err);
        }
    }

    async getConfiguration() {
        try {
            const response = await axios.get("/Configuration/RequirePatientDocumentTags");
            this.requirePatientDocumentTags = response.data;
        } catch (err) {
            console.error(err);
        }
    }

    toggleFilterTag(tag: string) {
        if (this.filterTags.includes(tag)) {
            this.filterTags = this.filterTags.filter(ff => ff !== tag);
        } else {
            this.filterTags.push(tag);
        }
    }

    getDocumentCountForTag(tag: string) {
        return this.patientDocuments
                   .filter(pd => pd.tags?.some(t => t.toLowerCase().trim() === tag)).length;
    }

    startEditDocument(item: PatientDocument, index: number) {
        this.editingPatientDocument = structuredClone(toRaw(item));
        this.editingRowIndex = index;
    }

    async editDocument() {
        try {
            await axios.post("/patient/edit-document", this.editingPatientDocument);

            this.loadDocs();
            this.editingRowIndex = -1;

        } catch (err) {
            console.error("Failed to edit patient document.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    close() {
        this.$emit('close');
    }
}

</script>

<style scoped>
::v-deep(table .actions) {
    width: 80px;
}

::v-deep(table .uploaded) {
    width: 260px;
}

::v-deep(table .file-name) {
    width: 40%;
}

::v-deep(table .tags) {
    width: 60%;
}


::v-deep(.badge-secondary) {
    color: rgb(58, 58, 58);
}

::v-deep(table input) {
    background: white;
}

.field-filters {
    gap: 5px;
}

.field-filters .badge {
    cursor: pointer;
    font-size: .85em;
}

.field-filters .badge .count {
    font-weight: 500;
    font-size: .9em;
    font-variant-numeric: tabular-nums;
    margin-left: 3px;
}
</style>
